/**
 * Typed agent WebSocket chunks → chat message list + voice buffers.
 * Server sends { type, messageId?, data?, done? } instead of one chat msg per frame.
 */

export type ToolUseData = {
  name: string
  input?: Record<string, unknown>
  meta?: Record<string, unknown>
}

export type AgentChatLine =
  | { role: 'user' | 'assistant'; content: string; messageId?: string }
  | { role: 'status'; toolUse: ToolUseData; messageId?: string }

export type AgentChunk = {
  type: string
  messageId?: string
  data?: unknown
  done?: boolean
}

export type ChunkApplyResult = {
  messages?: AgentChatLine[]
  status?: 'typing' | 'idle' | { toolUse: ToolUseData }
  error?: string
  isComplete?: boolean
  isRejected?: boolean
  profileUpdateFields?: string[]
  /** Base64 mp3 fragment for this messageId (empty string + done ends the stream). */
  voice?: { messageId: string; b64: string; done: boolean }
  historyHydrated?: boolean
}

function asToolUse(data: unknown): ToolUseData | null {
  if (!data || typeof data !== 'object') return null
  const rec = data as Record<string, unknown>
  const name = rec['name']
  if (typeof name !== 'string' || !name.trim()) return null
  return {
    name,
    input: (rec['input'] as Record<string, unknown>) || undefined,
    meta: (rec['meta'] as Record<string, unknown>) || undefined,
  }
}

function historyToMessages(data: unknown): AgentChatLine[] {
  if (!Array.isArray(data)) return []
  const out: AgentChatLine[] = []
  for (const item of data) {
    if (!item || typeof item !== 'object') continue
    const rec = item as Record<string, unknown>
    const role = rec['role']
    const messageId = typeof rec['messageId'] === 'string' ? rec['messageId'] : undefined
    if (role === 'status' && rec['toolUse']) {
      const tool = asToolUse(rec['toolUse'])
      if (tool) out.push({ role: 'status', toolUse: tool, messageId })
      continue
    }
    if ((role === 'user' || role === 'assistant') && typeof rec['content'] === 'string') {
      const content = (rec['content'] as string).trim()
      if (content) out.push({ role, content, messageId })
    }
  }
  return out
}

/**
 * Apply one server chunk. Returns a partial patch for the store/UI.
 * For `text`, concatenates into the last assistant row with the same messageId.
 */
export function applyAgentChunk(prev: AgentChatLine[], chunk: AgentChunk): ChunkApplyResult {
  const type = chunk.type

  if (type === 'typing') {
    return { status: 'typing' }
  }

  if (type === 'toolStart') {
    const tool = asToolUse(chunk.data)
    if (!tool) return {}
    return { status: { toolUse: tool } }
  }

  if (type === 'tool') {
    const tool = asToolUse(chunk.data)
    if (!tool) return { status: 'idle' }
    return {
      status: 'idle',
      messages: [...prev, { role: 'status', toolUse: tool, messageId: chunk.messageId }],
    }
  }

  if (type === 'history') {
    return {
      messages: historyToMessages(chunk.data),
      status: 'idle',
      historyHydrated: true,
    }
  }

  if (type === 'error') {
    const err =
      typeof chunk.data === 'string'
        ? chunk.data
        : chunk.data != null
          ? String(chunk.data)
          : 'Unknown error'
    return { error: err, status: 'idle' }
  }

  if (type === 'complete') {
    const data =
      chunk.data && typeof chunk.data === 'object' ? (chunk.data as Record<string, unknown>) : {}
    return {
      status: 'idle',
      isComplete: data['isComplete'] !== false,
      isRejected: data['isRejected'] === true,
    }
  }

  if (type === 'profileUpdate') {
    const data =
      chunk.data && typeof chunk.data === 'object' ? (chunk.data as Record<string, unknown>) : {}
    const fields = Array.isArray(data['updatedFields']) ? (data['updatedFields'] as string[]) : []
    return { profileUpdateFields: fields }
  }

  // Member text from any client on this thread (server fan-out).
  if (type === 'user') {
    const piece = typeof chunk.data === 'string' ? chunk.data.trim() : ''
    if (!piece) return {}
    const messageId = chunk.messageId
    const next = [...prev]
    const last = next[next.length - 1]
    // Sender already optimistically appended — attach id or skip exact duplicate.
    if (last && last.role === 'user' && last.content.trim() === piece) {
      if (messageId && !last.messageId) {
        next[next.length - 1] = { role: 'user', content: last.content, messageId }
        return { messages: next, status: 'idle' }
      }
      if (!messageId || last.messageId === messageId) return { status: 'idle' }
    }
    if (messageId && next.some((m) => m.role === 'user' && m.messageId === messageId)) {
      return { status: 'idle' }
    }
    next.push({ role: 'user', content: piece, messageId })
    return { messages: next, status: 'idle' }
  }

  if (type === 'text') {
    const raw = typeof chunk.data === 'string' ? chunk.data : ''
    const messageId = chunk.messageId
    if (!raw && !chunk.done) return {}

    const next = [...prev]
    const last = next[next.length - 1]
    if (last && last.role === 'assistant' && messageId && last.messageId === messageId) {
      next[next.length - 1] = {
        role: 'assistant',
        messageId,
        content: last.content + raw,
      }
      return { messages: next, status: 'idle' }
    }

    // New bubble: trim edges (legacy full-message frames often have padding).
    const piece = raw.trim()
    if (!piece) return { status: 'idle' }

    // Consecutive exact-duplicate assistant lines (legacy re-sends).
    if (
      last &&
      last.role === 'assistant' &&
      last.content.trim() === piece &&
      (!messageId || !last.messageId || last.messageId === messageId)
    ) {
      return { status: 'idle' }
    }

    next.push({ role: 'assistant', content: piece, messageId })
    return { messages: next, status: 'idle' }
  }

  if (type === 'voice') {
    const messageId = chunk.messageId
    if (!messageId) return {}
    const b64 = typeof chunk.data === 'string' ? chunk.data : ''
    return {
      voice: { messageId, b64, done: !!chunk.done },
    }
  }

  // Legacy shapes (pre-chunk protocol) — keep clients tolerant during deploy.
  if (typeof (chunk as { message?: string }).message === 'string') {
    const legacy = chunk as { message?: string; history?: unknown; error?: string }
    if (legacy.error) return { error: legacy.error, status: 'idle' }
    if (Array.isArray(legacy.history)) {
      return {
        messages: historyToMessages(legacy.history),
        status: 'idle',
        historyHydrated: true,
      }
    }
    if (legacy.message?.trim()) {
      return {
        messages: [...prev, { role: 'assistant', content: legacy.message.trim() }],
        status: 'idle',
      }
    }
  }

  return {}
}
